import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  ContentLayout,
  Header,
  Tabs,
  BreadcrumbGroup,
  Button,
} from '@amzn/awsui-components-react';
import { ActivitySidebar } from './sideNav';
import { CoordinatorEventShiftsList } from './coordinatorEventShiftsList';
import { TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE } from '../../utils/constants';
import { useAttendeeSessionStorage } from '../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../hooks/attendance/participantSessionStorage';

type TabType = 'upcoming' | 'past';

export function CoordinatorPage() {
  const { t } = useTranslation(['activity', 'translation']);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetAttendees } = useAttendeeSessionStorage();
  const { resetParticipants } = useParticipantSessionStorage();

  const urlFeatures = searchParams.get('features');
  const coordinatorActivityUrl = `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  const tabParam = searchParams.get('tab') as TabType | null;
  const activeTabId: TabType =
    tabParam && ['upcoming', 'past'].includes(tabParam) ? tabParam : 'upcoming';

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={false}
      navigation={<ActivitySidebar baseUrl={coordinatorActivityUrl} />}
      toolsHide={true}
      breadcrumbs={
        <div className="breadcrumbs">
          <BreadcrumbGroup
            items={[
              { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
              { text: t('breadcrumbs.coordinatorActivity'), href: '#' },
            ]}
            onFollow={(event) => {
              navigate(event.detail.href);
              event.preventDefault();
            }}
          />
        </div>
      }
      content={
        <div className="page-content">
          <ContentLayout
            header={
              <div className="page-content-header">
                <Header
                  variant="h1"
                  actions={
                    <Button
                      variant="primary"
                      href={`/track-attendance${urlFeatures ? '?features=' + urlFeatures : ''}`}
                      data-aci-analytics-name={TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE}
                      onFollow={() => {
                        // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
                        resetAttendees();
                        resetParticipants();
                      }}
                    >
                      {t('trackUnlistedEvent')}
                    </Button>
                  }
                >
                  {t('pageTitle.coordinatorActivity')}
                </Header>
              </div>
            }
          >
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('tab', detail.activeTabId);
                setSearchParams(newParams);
              }}
              tabs={[
                {
                  label: t('upcoming'),
                  id: 'upcoming',
                  content: (
                    <CoordinatorEventShiftsList
                      startDate={new Date(Date.now())}
                      sort="oldest-first"
                      isPastTab={false}
                    />
                  ),
                },
                {
                  label: t('past'),
                  id: 'past',
                  content: (
                    <CoordinatorEventShiftsList
                      endDate={new Date(Date.now())}
                      sort="newest-first"
                      isPastTab={true}
                    />
                  ),
                },
              ]}
            />
          </ContentLayout>
        </div>
      }
      contentType="cards"
    />
  );
}
